import { Bill, BillData, BillStatus, Trip, Apartment, Traveller } from '@/types';
import * as billRepository from '@/repository/billRepository';
import { getBillDataForTrip } from './billCalculationService';
import { getSingleApartmentBillData } from './optimizedBillService';
import { billEventService } from './billEventService';

export const billService = {
  listenToBills: (
    tripId: string, 
    callback: (bills: Bill[]) => void
  ): (() => void) => {
    return billRepository.listenToBills(tripId, callback);
  },

  listenToBillsByApartment: (
    tripId: string, 
    apartmentId: string, 
    callback: (bills: Bill[]) => void
  ): (() => void) => {
    return billRepository.listenToBillsByApartment(tripId, apartmentId, callback);
  },

  getBill: (
    tripId: string, 
    billId: string
  ): Promise<Bill | null> => {
    return billRepository.getBill(tripId, billId);
  },

  generateBill: async (
    tripId: string,
    apartmentId: string,
    trip: Trip,
    apartments: Apartment[],
    travellers: Traveller[],
    generatedBy: string,
    generatedByName?: string
  ): Promise<string> => {
    // Use optimized version that only calculates for the specific apartment
    const apartmentBillData = getSingleApartmentBillData(apartmentId, trip, apartments, travellers);
    
    if (!apartmentBillData) {
      throw new Error('No bill data found for apartment');
    }

    // Get apartment travelers with their IDs (only filter once)
    const apartmentTravellers = travellers.filter(t => t.apartmentId === apartmentId);
    
    // Create the initial "Bill Created" event
    const billCreatedEvent = billEventService.createBillCreatedEvent(generatedBy, generatedByName);
    
    // Create bill from current data
    const bill: Omit<Bill, 'id'> = {
      tripId,
      apartmentId,
      apartmentName: apartmentBillData.apartmentName,
      travellers: apartmentBillData.travellers.map((traveller, index) => ({
        id: apartmentTravellers[index]?.id || `temp-${index}`,
        name: traveller.name,
        accommodationCost: traveller.accommodationCost,
        skiPassCost: traveller.skiPassCost,
        transportationCost: traveller.transportationCost,
        membershipFee: traveller.membershipFee,
        total: traveller.total,
      })),
      totalAccommodation: apartmentBillData.totalAccommodation,
      totalSkiPass: apartmentBillData.totalSkiPass,
      totalTransportation: apartmentBillData.totalTransportation,
      totalMembership: apartmentBillData.totalMembership,
      grandTotal: apartmentBillData.grandTotal,
      status: BillStatus.GENERATED,
      isPaid: false,
      paidAmount: 0,
      generatedAt: new Date(),
      generatedBy,
      events: [billCreatedEvent],
      paymentTransactions: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      // Note: sentAt and paidAt are optional and not set for newly generated bills
    };

    return billRepository.addBill(tripId, bill);
  },

  updateBillStatus: async (
    tripId: string, 
    billId: string, 
    status: BillStatus,
    triggeredBy?: string,
    triggeredByName?: string
  ): Promise<void> => {
    // Get the current bill to access its events
    const currentBill = await billRepository.getBill(tripId, billId);
    if (!currentBill) {
      throw new Error('Bill not found');
    }

    const updateData: Partial<Pick<Bill, 'status' | 'sentAt' | 'events'>> = { status };
    const newEvents = [...currentBill.events];
    
    if (status === BillStatus.SENT) {
      updateData.sentAt = new Date();
      // Create a "Bill Sent" event
      const billSentEvent = billEventService.createBillSentEvent(
        triggeredBy || currentBill.generatedBy,
        triggeredByName
      );
      newEvents.push(billSentEvent);
      updateData.events = newEvents;
    }
    
    return billRepository.updateBill(tripId, billId, updateData);
  },

  updatePaymentStatus: async (
    tripId: string, 
    billId: string, 
    isPaid: boolean, 
    paidAmount: number = 0,
    triggeredBy?: string,
    triggeredByName?: string,
    paymentMethod?: string,
    reference?: string
  ): Promise<void> => {
    // Get the current bill to access its events and calculate remaining amount
    const currentBill = await billRepository.getBill(tripId, billId);
    if (!currentBill) {
      throw new Error('Bill not found');
    }

    const updateData: Partial<Pick<Bill, 'isPaid' | 'paidAmount' | 'paidAt' | 'status' | 'events'>> = {
      isPaid,
      paidAmount,
    };

    const newEvents = [...currentBill.events];
    const remainingAmount = currentBill.grandTotal - paidAmount;

    if (isPaid && paidAmount >= currentBill.grandTotal) {
      updateData.paidAt = new Date();
      updateData.status = BillStatus.FULLY_PAID;
      
      // Create "Fully Paid" event
      const fullyPaidEvent = billEventService.createFullyPaidEvent(
        currentBill.grandTotal,
        triggeredBy,
        triggeredByName,
        paidAmount - currentBill.paidAmount // This payment amount
      );
      newEvents.push(fullyPaidEvent);
    } else if (paidAmount > currentBill.paidAmount) {
      updateData.status = BillStatus.PARTIALLY_PAID;
      
      // Create "Payment Received" event for the additional payment
      const paymentAmount = paidAmount - currentBill.paidAmount;
      const paymentReceivedEvent = billEventService.createPaymentReceivedEvent(
        paymentAmount,
        paidAmount,
        remainingAmount,
        triggeredBy,
        triggeredByName,
        paymentMethod,
        reference
      );
      newEvents.push(paymentReceivedEvent);
    }

    updateData.events = newEvents;
    return billRepository.updateBill(tripId, billId, updateData);
  },

  deleteBill: (
    tripId: string, 
    billId: string
  ): Promise<void> => {
    return billRepository.deleteBill(tripId, billId);
  },
};
